import { FormEvent, useState } from 'react'
import { FaCheckCircle, FaCopy, FaMobileAlt, FaShieldAlt } from 'react-icons/fa'
import { QRCodeSVG } from 'qrcode.react'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Field, IconButton, Input, Modal, StatusPill, Steps, useToast } from 'ui'
import { apiErrorCode, apiErrorMessage, formatDateTime, ReauthBanner, UserProfile } from './shared'
import s from './styles.module.scss'

interface Props {
    profile: UserProfile
    onChange: (patch: Partial<UserProfile>) => void
}

interface SetupPayload {
    secret: string
    otpauthUrl: string
    issuer: string
    account: string
}

/** Group a base32 secret into blocks of 4 for manual entry. */
const groupSecret = (secret: string) => secret.replace(/(.{4})/g, '$1 ').trim()

/**
 * Authenticator-app (TOTP) two-factor authentication. Enrolment runs in a
 * modal: scan QR → enter first code → done. Cognito prompts for the code at
 * every sign-in from then on.
 */
export const TwoFactorCard = ({ profile, onChange }: Props) => {
    const { toast } = useToast()
    const enabled = profile.mfa.enabled

    const [setupOpen, setSetupOpen] = useState(false)
    const [disableOpen, setDisableOpen] = useState(false)
    const [setup, setSetup] = useState<SetupPayload | null>(null)
    const [step, setStep] = useState(0)
    const [code, setCode] = useState('')
    const [deviceName, setDeviceName] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reauth, setReauth] = useState(false)
    const [copied, setCopied] = useState(false)

    const resetSetup = () => {
        setSetup(null)
        setStep(0)
        setCode('')
        setDeviceName('')
        setError(null)
        setCopied(false)
    }

    const beginSetup = async () => {
        setError(null)
        setReauth(false)
        setBusy(true)
        try {
            const response = await api({ url: '/profile-mfa-setup', data: {} })
            if (!response.data?.success) throw new Error(response.data?.error || 'Could not start setup')
            setSetup(response.data)
            setStep(0)
            setSetupOpen(true)
        } catch (err: any) {
            if (apiErrorCode(err) === 'REAUTH_REQUIRED') setReauth(true)
            else setError(apiErrorMessage(err, 'Could not start two-factor setup.'))
        } finally {
            setBusy(false)
        }
    }

    const confirmSetup = async (event: FormEvent) => {
        event.preventDefault()
        const digits = code.replace(/\s+/g, '')
        if (busy || !/^\d{6}$/.test(digits)) return
        setBusy(true)
        setError(null)
        try {
            const response = await api({ url: '/profile-mfa-confirm', data: { code: digits, deviceName: deviceName.trim() } })
            if (!response.data?.success) throw new Error(response.data?.error || 'Verification failed')
            onChange({
                mfa: { ...profile.mfa, enabled: true, methods: ['SOFTWARE_TOKEN_MFA'], preferred: 'SOFTWARE_TOKEN_MFA', enabledAt: new Date().toISOString() },
            })
            setStep(2)
            toast({ title: 'Two-factor authentication is on', description: "You'll be asked for a code next time you sign in.", tone: 'good' })
        } catch (err: any) {
            const errCode = apiErrorCode(err)
            if (errCode === 'REAUTH_REQUIRED') {
                setReauth(true)
                setSetupOpen(false)
            } else {
                setError(apiErrorMessage(err, "That code wasn't right."))
            }
            setCode('')
        } finally {
            setBusy(false)
        }
    }

    const disable = async () => {
        if (busy) return
        setBusy(true)
        setError(null)
        try {
            const response = await api({ url: '/profile-mfa-disable', data: {} })
            if (!response.data?.success) throw new Error(response.data?.error || 'Could not turn off')
            onChange({ mfa: { ...profile.mfa, enabled: false, methods: [], preferred: null, enabledAt: null } })
            setDisableOpen(false)
            toast({ title: 'Two-factor authentication is off', description: 'Your account is protected by your password only.', tone: 'warn' })
        } catch (err: any) {
            if (apiErrorCode(err) === 'REAUTH_REQUIRED') {
                setReauth(true)
                setDisableOpen(false)
            } else {
                setError(apiErrorMessage(err, 'Could not turn off two-factor authentication.'))
            }
        } finally {
            setBusy(false)
        }
    }

    const copySecret = async () => {
        if (!setup) return
        try {
            await navigator.clipboard.writeText(setup.secret)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2000)
        } catch {
            setError('Could not copy — select the key and copy it manually.')
        }
    }

    const closeSetup = () => {
        setSetupOpen(false)
        resetSetup()
    }

    return (
        <>
            <Card
                title="Two-factor authentication"
                subtitle="Adds a second check at sign-in: a 6-digit code from an authenticator app on your phone."
                actions={
                    <StatusPill tone={enabled ? 'good' : 'neutral'} dot size="sm">
                        {enabled ? 'On' : 'Off'}
                    </StatusPill>
                }
            >
                <div className={s.form}>
                    {reauth && <ReauthBanner action="two-factor authentication" />}
                    {error && !setupOpen && !disableOpen && (
                        <Banner tone="bad" title="Something went wrong">
                            {error}
                        </Banner>
                    )}

                    <div className={s.mfaStatus}>
                        <span className={`${s.mfaIcon} ${enabled ? s.mfaIconOn : ''}`} aria-hidden="true">
                            <FaShieldAlt />
                        </span>
                        <div className={s.mfaText}>
                            {enabled ? (
                                <>
                                    <strong>Authenticator app is active</strong>
                                    <span>
                                        {profile.mfa.enabledAt
                                            ? `Turned on ${formatDateTime(profile.mfa.enabledAt)}. `
                                            : ''}
                                        Every sign-in needs your password and a code from your app.
                                    </span>
                                </>
                            ) : (
                                <>
                                    <strong>Not set up</strong>
                                    <span>
                                        Works with Google Authenticator, Microsoft Authenticator, 1Password, Authy and any other TOTP app.
                                        Strongly recommended for administrator accounts.
                                    </span>
                                </>
                            )}
                        </div>
                        <div className={s.mfaActions}>
                            {enabled ? (
                                <>
                                    <Button size="sm" onClick={() => void beginSetup()} loading={busy && !setupOpen}>
                                        Change app
                                    </Button>
                                    <Button size="sm" variant="danger" onClick={() => setDisableOpen(true)} disabled={busy}>
                                        Turn off
                                    </Button>
                                </>
                            ) : (
                                <Button size="sm" variant="primary" icon={<FaMobileAlt />} onClick={() => void beginSetup()} loading={busy && !setupOpen}>
                                    Set up
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            <Modal
                open={setupOpen}
                onClose={closeSetup}
                title={enabled ? 'Change authenticator app' : 'Set up two-factor authentication'}
                subtitle={
                    step === 2
                        ? undefined
                        : enabled
                          ? 'Scanning a new code replaces the app currently linked to your account once you confirm.'
                          : 'Takes about a minute. Have your phone ready.'
                }
                size="md"
                busy={busy}
                footer={
                    step === 2 ? (
                        <Button variant="primary" onClick={closeSetup}>
                            Done
                        </Button>
                    ) : step === 0 ? (
                        <>
                            <Button variant="ghost" onClick={closeSetup}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={() => setStep(1)}>
                                I've scanned it
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={() => setStep(0)} disabled={busy}>
                                Back
                            </Button>
                            <Button variant="primary" form="mfa-confirm-form" type="submit" loading={busy} disabled={!/^\d{6}$/.test(code.replace(/\s+/g, ''))}>
                                Verify and turn on
                            </Button>
                        </>
                    )
                }
            >
                {setup && (
                    <div className={s.form}>
                        {step < 2 && <Steps steps={[{ label: 'Scan' }, { label: 'Verify' }, { label: 'Done' }]} current={step} />}

                        {step === 0 && (
                            <div className={s.mfaScan}>
                                <div className={s.qrWrap}>
                                    <QRCodeSVG value={setup.otpauthUrl} size={180} level="M" includeMargin={false} />
                                </div>
                                <div className={s.mfaScanText}>
                                    <p>
                                        Open your authenticator app, choose <strong>Add account</strong> and scan this code.
                                    </p>
                                    <p className={s.mfaHint}>Can't scan? Enter this key manually instead:</p>
                                    <div className={s.secretRow}>
                                        <code className={s.secret}>{groupSecret(setup.secret)}</code>
                                        <IconButton
                                            label={copied ? 'Copied' : 'Copy key'}
                                            icon={copied ? <FaCheckCircle /> : <FaCopy />}
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => void copySecret()}
                                        />
                                    </div>
                                    <p className={s.mfaHint}>
                                        Account: {setup.account} · Type: time-based (TOTP)
                                    </p>
                                </div>
                            </div>
                        )}

                        {step === 1 && (
                            <form id="mfa-confirm-form" className={s.form} onSubmit={confirmSetup} noValidate>
                                {error && (
                                    <Banner tone="bad" title="Couldn't verify">
                                        {error}
                                    </Banner>
                                )}
                                <Field label="6-digit code from your app" required hint="Codes change every 30 seconds — enter the one showing now.">
                                    {(id) => (
                                        <Input
                                            id={id}
                                            className={s.codeInput}
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, '').slice(0, 7))}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            placeholder="123 456"
                                            mono
                                            autoFocus
                                            disabled={busy}
                                        />
                                    )}
                                </Field>
                                <Field label="Name this device" optional hint="Helps you remember which phone holds the code, e.g. “Work iPhone”.">
                                    {(id) => (
                                        <Input
                                            id={id}
                                            value={deviceName}
                                            onChange={(e) => setDeviceName(e.target.value)}
                                            maxLength={60}
                                            placeholder="Work iPhone"
                                            disabled={busy}
                                        />
                                    )}
                                </Field>
                            </form>
                        )}

                        {step === 2 && (
                            <div className={s.mfaDone}>
                                <FaCheckCircle aria-hidden="true" />
                                <div>
                                    <strong>Two-factor authentication is on</strong>
                                    <p>
                                        From now on, after entering your password you'll be asked for the 6-digit code from{' '}
                                        {deviceName.trim() ? <strong>{deviceName.trim()}</strong> : 'your authenticator app'}. Keep the
                                        app installed — if you lose access to it, an administrator will need to reset two-factor for
                                        you.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                open={disableOpen}
                onClose={() => setDisableOpen(false)}
                title="Turn off two-factor authentication?"
                subtitle="Your account will be protected by your password alone."
                size="sm"
                busy={busy}
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setDisableOpen(false)} disabled={busy}>
                            Keep it on
                        </Button>
                        <Button variant="danger" onClick={() => void disable()} loading={busy}>
                            Turn off
                        </Button>
                    </>
                }
            >
                {error && (
                    <Banner tone="bad" title="Couldn't turn off">
                        {error}
                    </Banner>
                )}
                <p className={s.modalText}>
                    You can turn it back on at any time by scanning a new code. We'll email you to confirm the change.
                </p>
            </Modal>
        </>
    )
}
